import Title from "antd/es/typography/Title";
import { useStoreFileMutation } from "api/File/hooks.ts";
import queryClient from "api/queryClient";
import { useGetOfferQuery, useUpdateOfferMutation } from "api/TenantSlug/Offer/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { AddButton } from "components/UI/Table/UI/AddButton/AddButton.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useRef } from "react";
import { useWatch } from "react-hook-form";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { OfferAccess, OfferBalanceAffiliateBilling, OfferStatus } from "types/enums.ts";
import { DrawerId, FormId } from "types/models";

import { OfferEditGeneralFormData, schema } from "./formSchema.ts";
import { AdvertiserSection } from "./sections/AdvertiserSection";
import { CommissionsSection, CommissionsSectionActions } from "./sections/CommissionsSection";
import { DescriptionSection } from "./sections/DescriptionSection";
import { InformationSection } from "./sections/InformationSection";
import { RulesSection } from "./sections/RulesSection";
import { SettingsSection } from "./sections/SettingsSection";

import OfferData = App.Data.OfferData;
import UpdateOfferRequest = App.Http.Requests.Offer.UpdateOfferRequest;

interface OfferGeneralEditFormContentProps {
    offerId: string;
    offer?: OfferData;
    onLogoSelected: (file: File | null) => void;
}

const OfferGeneralEditFormContent: FC<OfferGeneralEditFormContentProps> = ({
    offerId,
    offer: editOffer,
    onLogoSelected,
}) => {
    const expandButtonRef = useRef<() => void>(null);
    const { t: tF } = usePaldockTranslation("forms.offer-general-edit");
    const { t: tC } = usePaldockTranslation("common");

    const { control } = useGeneralForm<OfferEditGeneralFormData>();

    // Watch access field for conditional rendering
    const access = useWatch({ control, name: "access" });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="flex flex-col gap-6">
                {/* Advertiser */}
                <SectionContentWrapper withBackground className="h-[320px] overflow-y-auto flex flex-col">
                    <Title level={3} className="!mb-6">
                        {tF("sections.advertiser.title")}
                    </Title>
                    <AdvertiserSection control={control} onLogoSelected={onLogoSelected} />
                </SectionContentWrapper>

                {/* Settings */}
                <SectionContentWrapper withBackground className="h-[250px] overflow-y-auto flex flex-col">
                    <Title level={3} className="!mb-6">
                        {tC("settings")}
                    </Title>
                    <SettingsSection control={control} access={access} />
                </SectionContentWrapper>

                {/* Description */}
                <SectionContentWrapper withBackground className="h-[690px] overflow-y-auto flex flex-col">
                    <Title level={3} className="!mb-6">
                        {tF("sections.description.title")}
                    </Title>
                    <DescriptionSection control={control} />
                </SectionContentWrapper>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-6">
                {/* Commissions */}
                <SectionContentWrapper withBackground className="flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <Title level={3} className="!mb-0">
                            {tF("sections.commissions.title")}
                        </Title>
                        <CommissionsSectionActions expandButtonRef={expandButtonRef} />
                    </div>
                    <CommissionsSection offer={editOffer} expandButtonRef={expandButtonRef} scrollY={400} />
                </SectionContentWrapper>

                {/* Information */}
                <SectionContentWrapper withBackground className="h-[380px] overflow-y-auto flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <Title level={3} className="!mb-0">
                            {tF("sections.information.title")}
                        </Title>
                        <AddButton drawerId={DrawerId.AddCustomInformationField} />
                    </div>
                    <InformationSection offerId={offerId} />
                </SectionContentWrapper>

                {/* Rules */}
                <SectionContentWrapper withBackground className="h-[380px] overflow-y-auto flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <Title level={3} className="!mb-0">
                            {tF("sections.rules.title")}
                        </Title>
                        <AddButton drawerId={DrawerId.AddCustomRuleField} />
                    </div>
                    <RulesSection offerId={offerId} />
                </SectionContentWrapper>
            </div>
        </div>
    );
};

export const OfferGeneralEditForm: FC<{ offerId: string }> = ({ offerId }) => {
    const { data, isLoading, isError } = useGetOfferQuery(offerId, {
        filter: { include: "advertiser,labels,country,affiliates" },
    });
    const editOffer = data?.offer;
    const updateOfferMutation = useUpdateOfferMutation(offerId);
    const storeFileMutation = useStoreFileMutation();
    const pendingLogoFile = useRef<File | null>(null);
    const logoCleared = useRef(false);
    const { t: tF } = usePaldockTranslation("forms.offer-general-edit");

    const formId = FormId.OfferGeneralEditForm;

    const handleEditOffer = (data: UpdateOfferRequest) => {
        updateOfferMutation.mutate(data, {
            onSuccess: () => {
                toast.success(tF("messages.update_success"));
                queryClient.invalidateQueries({
                    queryKey: ["getOffer", offerId],
                });
            },
            onError: (error) => {
                console.error("Update offer failed:", error);
                toast.error(tF("messages.update_error"));
            },
        });
    };

    const onSubmit = async (values: OfferEditGeneralFormData) => {
        let logoPath: string | null | undefined = undefined;
        if (pendingLogoFile.current) {
            const formData = new FormData();
            formData.append("file", pendingLogoFile.current);
            formData.append("type", "offer_logo");
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - formData as StoreFileRequest
                const res = await storeFileMutation.mutateAsync(formData);
                logoPath = res.path;
                pendingLogoFile.current = null;
                logoCleared.current = false;
            } catch (error) {
                console.error("Offer logo upload failed:", error);
                toast.error(tF("messages.update_error"));
                return;
            }
        } else if (logoCleared.current) {
            logoPath = null;
            logoCleared.current = false;
        }

        const data: UpdateOfferRequest = {
            title: values.title,
            visible_title: values.visible_title,
            description: values.description,
            access: values.access ?? OfferAccess.Private,
            status: values.status ?? OfferStatus.Disabled,
            logo: logoPath,
            advertiser_id: values.advertiser_id,
            currency_id: values.currency_id,
            label_ids: values.label_ids,
            affiliate_ids:
                values.access === OfferAccess.Private || values.access === OfferAccess.Restricted
                    ? values.partners
                    : undefined,
            country_id: values.country_id ?? null,
            category_id: values.category ?? null,
            affiliate_billing: values.affiliate_billing,
        };

        handleEditOffer(data);
    };

    if (!editOffer) {
        return <QueryState isLoading={isLoading} isError={isError || !isLoading} errorKey="errors.loading_offer" />;
    }

    const defaultValues: OfferEditGeneralFormData = {
        title: editOffer.title ?? "",
        visible_title: editOffer.visible_title ?? "",
        advertiser_id: editOffer.advertiser?.id ?? editOffer.advertiser_id,
        currency_id: editOffer.currency_id ?? editOffer.currency?.id ?? 40,
        country_id: editOffer.country?.id ?? editOffer.country_id ?? null,
        category: editOffer.category_id ?? undefined,
        logo: editOffer.logo?.url ?? null,
        label_ids: editOffer.labels?.map((l) => l.id) ?? [],
        status: editOffer.status ?? OfferStatus.Disabled,
        enabled_from: null,
        integrations: [],
        access: editOffer.access ?? OfferAccess.Private,
        partners: editOffer.affiliates?.map((a) => a.id) ?? [],
        description: editOffer.description ?? null,
        affiliate_billing: editOffer.affiliate_billing ?? OfferBalanceAffiliateBilling.TenantSetting,
    };

    return (
        <GeneralFormProvider<OfferEditGeneralFormData>
            key={offerId}
            schema={schema}
            defaultValues={defaultValues}
            id={formId}
            onSubmit={onSubmit}
            resetOnSubmit={false}
            requiredMark={false}
        >
            <OfferGeneralEditFormContent
                offerId={offerId}
                offer={editOffer}
                onLogoSelected={(f) => {
                    pendingLogoFile.current = f;
                    logoCleared.current = f === null;
                }}
            />
        </GeneralFormProvider>
    );
};
